import { useEffect, useRef, useState } from "react"
import { startCheckout } from "../lib/api"
import { displayDestination } from "../lib/urls"

// Pricing decision surface shown in place over the marketing hero when an
// anonymous visitor hits a Pro-locked control (a module/eye shape, the wordmark
// removal link). It replaces the old full-page jump to /pricing, which stranded
// the visitor away from the code they'd just built. Dismiss → back to the hero,
// code intact. "Choose Pro" carries the code's token into the checkout flow
// (`/billing/checkout?period=…&claim=<token>`) so, after OAuth + Stripe, the
// code follows the new account and its editor re-opens — see BillingController
// and Dashboard's `?code=` deep-link.
//
// The signed-in app opens the same box from every upgrade button (`signedIn`,
// mounted once by UpgradeProvider). Those buttons used to start a monthly
// checkout directly, so the owner reached the card form without the price, the
// annual option or the line that the code keeps working (#336). Signed in,
// "Choose Pro" starts the checkout here: this is the one place in the app that
// does.
//
// Modal chrome (native <dialog>: Esc, scrim, focus-trap, focus-restore, body
// scroll lock, the fade/translate transition) mirrors HeroResult's capture
// modal so the two feel identical.

// The headline is two lines. The first is the same for every entry point and
// says the code is free and keeps working. The second, with the sub-line under
// it, is keyed by the control the visitor clicked (`reason`). One headline for
// every entry point answered a click on the wordmark link with a line about
// shapes, and "Keep your code." under an upgrade title read as "pay to keep
// your code" (#268). No line here ties the code's future to paying. An unknown
// or missing reason gets `default`.
const FREE_FIRST = "Your code is free and keeps working."

const COPY = {
  wordmark: {
    pro: "Pro removes the tangoqr mark.",
    sub: "The mark is the small tangoqr name in the corner of the image. Every free code has it."
  },
  shapes: {
    pro: "Pro adds more shapes.",
    sub: "The square style is free on every code. Rounded, dot, leaf and circle styles come with Pro."
  },
  default: {
    pro: "Here is what Pro adds.",
    sub: "Free codes never expire. Compare the two plans below."
  }
}

// The edit lock on a code past its free 50 scans (#336): the lock wall, the
// locked pencil in the dashboard builder, and the bulk repoint's skipped codes.
// The question at that moment is what happens to the code without Pro, so the
// sub-line answers it and names the link when the button knows it.
function lockedCopy(destination) {
  const to = displayDestination(destination)
  if (!to) {
    return {
      pro: "Editing a code past its first 50 scans needs Pro.",
      sub: "It opens the same link whether you pay or not. If you cancel Pro, it still does."
    }
  }
  return {
    pro: "Editing this code needs Pro.",
    sub: (
      <>
        It opens <span className="font-mono break-all text-coal dark:text-cloud">{to}</span> whether you pay or not.
        If you cancel Pro, it still does.
      </>
    )
  }
}

function copyFor(reason, destination) {
  if (reason === "locked") return lockedCopy(destination)
  return COPY[reason] || COPY.default
}

const PRICE = {
  monthly: { amount: "$10", unit: "/mo", note: null },
  annual:  { amount: "$100", unit: "/yr", note: "Save 17% — two months free" }
}

const FREE_FEATURES = [
  "Unlimited codes",
  "Full editing + analytics per code, first 50 scans",
  "Custom logo and colors",
  "Square modules and eyes",
]

const PRO_FEATURES = [
  "Edit + detailed analytics on every code, no scan limit",
  "No tangoqr mark on the image",
  "Module + eye shape styles: rounded, dots, circles",
  "Unlimited codes, custom logo, colors",
]

function Check() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-0.5 flex-shrink-0 text-voltage-dark dark:text-voltage-light">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

export default function PricingModal({ open, onClose, claimToken, reason, destination = null, signedIn = false }) {
  const [period, setPeriod] = useState("monthly")
  const [shown, setShown] = useState(false)
  const [starting, setStarting] = useState(false)
  const [checkoutError, setCheckoutError] = useState(null)
  const dialogRef = useRef(null)

  // Each opening starts clean, and closing puts focus back on the button that
  // opened the box. The box unmounts rather than close()s, so the dialog's own
  // focus-restore never runs; in the app the box can sit over another modal
  // (the edit modal's lock wall), which would be left with focus nowhere.
  // Declared before the showModal effect so it reads the opener before
  // showModal moves focus into the box.
  useEffect(() => {
    if (!open) return
    setStarting(false)
    setCheckoutError(null)
    const opener = document.activeElement
    return () => { if (opener?.isConnected) opener.focus?.() }
  }, [open])

  // Native <dialog> for Esc + focus-trap + focus-restore, mirroring HeroResult.
  // Rendered only while open, so the ref is present when this effect runs.
  useEffect(() => {
    if (!open) return
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    const raf = requestAnimationFrame(() => setShown(true))
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handleCancel = (e) => { e.preventDefault(); onClose() } // Esc
    const handleClick = (e) => { if (e.target === dialog) onClose() } // scrim
    dialog?.addEventListener("cancel", handleCancel)
    dialog?.addEventListener("click", handleClick)
    return () => {
      cancelAnimationFrame(raf)
      document.body.style.overflow = prevOverflow
      dialog?.removeEventListener("cancel", handleCancel)
      dialog?.removeEventListener("click", handleClick)
      setShown(false)
    }
  }, [open, onClose])

  if (!open) return null

  const copy = copyFor(reason, destination)
  const price = PRICE[period]
  const params = new URLSearchParams({ period })
  if (claimToken) params.set("claim", claimToken)
  const checkoutHref = `/billing/checkout?${params.toString()}`

  // Signed in: the JSON checkout, so a cancel returns to /app (the anchor's
  // cancel lands on /pricing) and a refusal shows here. startCheckout leaves
  // the page on success, so `starting` stays on through the redirect.
  async function choosePro() {
    setCheckoutError(null)
    setStarting(true)
    try {
      await startCheckout(period)
    } catch (e) {
      setStarting(false)
      setCheckoutError(e.message || "Couldn't start checkout. Try again.")
    }
  }
  const choiceClass = "mt-5 block w-full text-center px-5 py-3 rounded-lg bg-voltage text-coal font-semibold shadow-voltage hover:bg-voltage-dark transition no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-wait"

  return (
    <dialog
      ref={dialogRef}
      aria-label="Free and Pro plans"
      className="p-0 m-auto bg-transparent backdrop:bg-coal/60 backdrop:backdrop-blur-sm overflow-visible"
    >
      <div
        className={
          "w-[min(94vw,620px)] max-h-[92dvh] flex flex-col bg-white dark:bg-midnight text-ink dark:text-cloud " +
          "rounded-2xl shadow-lg border border-[color:var(--color-border)] dark:border-storm overflow-hidden " +
          "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] " +
          (shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2")
        }
      >
        <div className="flex-none flex justify-end px-3 pt-3">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition bg-transparent border-0 p-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto px-6 pb-6 pt-1">
          <div className="text-center">
            {/* A label for the two columns, not an instruction. "Upgrade to
                Pro" in capitals above the headline was the first thing read,
                ahead of the line that says the code is free. */}
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-plasma dark:text-plasma-light">
              Free and Pro
            </p>
            {/* One sentence per line, so the break never lands mid-sentence and
                the free line reads as a statement of its own. */}
            <h3 className="mt-1.5 font-display font-bold text-[23px] tracking-[-0.01em] text-coal dark:text-cloud text-balance">
              <span className="block">{FREE_FIRST}</span>{" "}
              <span className="block">{copy.pro}</span>
            </h3>
            <p className="mt-2 text-[14.5px] leading-[1.5] text-storm dark:text-muted-light text-balance">
              {copy.sub}
            </p>
          </div>

          {/* Billing period toggle — mirrors the pricing page's monthly/annual pill. */}
          <div className="mt-5 flex justify-center">
            <fieldset className="inline-flex items-center rounded-full border border-[color:var(--color-border)] dark:border-storm p-1 bg-cloud dark:bg-coal">
              <legend className="sr-only">Billing period</legend>
              {["monthly", "annual"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  aria-pressed={period === p}
                  className={
                    "px-5 py-1.5 rounded-full text-sm font-semibold capitalize transition focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 " +
                    (period === p
                      ? "bg-voltage text-coal"
                      : "text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud")
                  }
                >
                  {p}
                </button>
              ))}
            </fieldset>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch">
            {/* Free — current plan */}
            <div className="rounded-xl border border-[color:var(--color-border)] dark:border-storm p-5 flex flex-col">
              <div className="flex items-baseline justify-between">
                <h4 className="font-display font-semibold text-lg text-coal dark:text-cloud">Free</h4>
                <span className="text-xs font-semibold text-[color:var(--color-muted)] dark:text-muted-light">Current</span>
              </div>
              <p className="mt-1.5 font-mono text-2xl text-coal dark:text-cloud">$0</p>
              <ul className="mt-4 space-y-2 text-sm text-coal dark:text-cloud flex-1">
                {FREE_FEATURES.map((f) => (
                  <li key={f} className="flex gap-2"><Check />{f}</li>
                ))}
              </ul>
            </div>

            {/* Pro — highlighted, the upgrade */}
            <div className="rounded-xl border-2 border-voltage shadow-md p-5 flex flex-col relative">
              <p className="text-xs font-semibold uppercase tracking-wider text-plasma dark:text-plasma-light">Most popular</p>
              <div className="mt-1 flex items-baseline justify-between">
                <h4 className="font-display font-semibold text-lg text-coal dark:text-cloud">Pro</h4>
              </div>
              <p className="mt-1.5 font-mono text-2xl text-coal dark:text-cloud">
                {price.amount}<span className="text-base text-[color:var(--color-muted)] dark:text-muted-light">{price.unit}</span>
              </p>
              <p className="mt-1 h-4 text-xs font-semibold text-voltage-dark dark:text-voltage-light">
                {price.note || ""}
              </p>
              <ul className="mt-3 space-y-2 text-sm text-coal dark:text-cloud flex-1">
                {PRO_FEATURES.map((f) => (
                  <li key={f} className="flex gap-2"><Check />{f}</li>
                ))}
              </ul>
              {signedIn ? (
                <>
                  <button type="button" onClick={choosePro} disabled={starting} className={choiceClass}>
                    {starting ? "Opening checkout…" : "Choose Pro"}
                  </button>
                  {checkoutError && (
                    <p role="alert" className="mt-2 text-xs text-fail">{checkoutError}</p>
                  )}
                </>
              ) : (
                <a href={checkoutHref} className={choiceClass}>
                  Choose Pro
                </a>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-4 w-full text-center text-sm font-semibold text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
          >
            Maybe later
          </button>
          <p className="mt-3 text-center text-xs text-[color:var(--color-muted)] dark:text-muted-light">
            {signedIn
              ? "You pay on the next page. Cancel anytime from Profile."
              : "You finish and pay after you create an account. Cancel anytime."}
          </p>
        </div>
      </div>
    </dialog>
  )
}
